import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { useTranslation } from 'react-i18next';
import DocumentBoxViewer from './DocumentBoxViewer';
import { isSameDocument } from './fieldBoxes';

/**
 * The field-box viewer when the shown document has boxes, otherwise the host's regular preview
 * (renderFallback). Falls back for good on any viewer failure; the user can also switch to the
 * regular preview and back.
 */
export default function DocumentPreviewWithBoxes({ url, positions, renderFallback, ...viewerProps }) {
    const { t } = useTranslation();
    const [failedUrl, setFailedUrl] = useState(null);
    const [preferOriginal, setPreferOriginal] = useState(false);

    useEffect(() => {
        setFailedUrl(null);
    }, [url]);

    const boxesAvailable = Boolean(url && positions && failedUrl !== url && isSameDocument(url, positions.documentKey));

    if (boxesAvailable && !preferOriginal) {
        return (
            <DocumentBoxViewer
                key={url}
                url={url}
                positions={positions}
                {...viewerProps}
                onError={() => setFailedUrl(url)}
                onOpenOriginal={() => setPreferOriginal(true)}
            />
        );
    }

    return (
        <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
            {renderFallback()}
            {boxesAvailable && (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<CropFreeIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setPreferOriginal(false)}
                    sx={{
                        position: 'absolute',
                        bottom: 12,
                        insetInlineEnd: 12,
                        textTransform: 'none',
                        fontSize: 12,
                        boxShadow: 2,
                        '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                    }}
                >
                    {t('FieldBoxesViewer')}
                </Button>
            )}
        </Box>
    );
}

DocumentPreviewWithBoxes.propTypes = {
    url: PropTypes.string,
    /** Output of useFieldPositions (null = regular preview). */
    positions: PropTypes.object,
    /** The host's regular preview (e.g. an Iframe). */
    renderFallback: PropTypes.func.isRequired,
    visibleFields: PropTypes.instanceOf(Set),
    activeField: PropTypes.string,
    fieldLabels: PropTypes.object,
    fieldOrder: PropTypes.arrayOf(PropTypes.string),
    onFieldClick: PropTypes.func,
    /** true while boxes are drawn on the document (see DocumentBoxViewer). */
    onBoxesShownChange: PropTypes.func,
};
